'use client'

import { useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ArrowRight, Clock, MapPin, Phone, Search } from 'lucide-react'
import { formatImageURL } from '@/utils/util'
import { getBranchOpenStatus, summariseWorkingHours } from '@/utils/branch'
import { navigateUrl } from '@/utils/client-request'
import BranchCard from './BranchCard'

function FeaturedBranch({ branch }: { branch: any }) {
  const status = getBranchOpenStatus(branch?.workingHours)
  const hoursRows = summariseWorkingHours(branch?.workingHours)
  const todayLabel = hoursRows[0]?.time

  const mapHref = branch?.latitude && branch?.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${branch.latitude},${branch.longitude}`
    : branch?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branch.address)}` : null

  return (
    <section className="mt-10 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] bg-white rounded-3xl overflow-hidden">
      <div className="relative aspect-16/11 lg:aspect-auto lg:min-h-[460px]" style={{ background: 'linear-gradient(135deg, #BFD9FF 0%, #C8C9FF 100%)' }}>
        {branch?.image && (
          <img src={formatImageURL(branch.image)} alt={branch?.name || ''} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute top-6 left-7 text-[16px] font-semibold font-gilroy text-foreground/70">01</div>
        {branch.address && (
          <div className="absolute top-6 right-6 inline-flex items-center gap-1.5 bg-white text-foreground rounded-full px-3.5 py-2 text-[13px] font-medium">
            <MapPin className="size-3.5" /> {branch.address.split(',')[0]}
          </div>
        )}
        <div className="absolute bottom-6 left-7 inline-flex items-center gap-2 bg-foreground text-cream rounded-full pl-2.5 pr-3.5 py-2 text-[13px] font-medium">
          <span className={`w-2 h-2 rounded-full ${status.open ? 'bg-emerald-400' : 'bg-foreground/40'}`} style={status.open ? { boxShadow: '0 0 0 4px rgba(37,194,108,0.25)' } : undefined} />
          {status.open ? 'Одоо нээлттэй' : 'Хаалттай'}{todayLabel ? ` · ${todayLabel}` : ''}
        </div>
      </div>

      <div className="p-8 sm:p-10 flex flex-col gap-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55 mb-3">
            {branch.isDefault ? 'Үндсэн салбар' : 'Гол салбар'}
          </div>
          <h2 className="font-bold font-gilroy tracking-[-0.022em] leading-[1.05] text-foreground" style={{ fontSize: 'clamp(2rem, 3.4vw, 2.75rem)' }}>
            {branch.name}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 py-5 border-y border-foreground/10">
          {branch.address && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.08em] text-foreground/55 mb-1.5">Хаяг</div>
              <div className="text-[15px] font-medium text-foreground leading-[1.4]">{branch.address}</div>
            </div>
          )}
          {(() => {
            const phones = [branch.phone, ...(Array.isArray(branch.additionalPhones) ? branch.additionalPhones : [])].filter(Boolean)
            if (phones.length === 0) return null
            return (
              <div>
                <div className="text-[11px] uppercase tracking-[0.08em] text-foreground/55 mb-1.5">Утас</div>
                <div className="text-[15px] font-medium text-foreground leading-[1.4] flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {phones.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-2">
                      <a href={`tel:${p}`} className="hover:underline tabular-nums">{p}</a>
                      {i < phones.length - 1 && <span className="text-foreground/30">·</span>}
                    </span>
                  ))}
                </div>
              </div>
            )
          })()}
          {hoursRows.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.08em] text-foreground/55 mb-1.5">Цагийн хуваарь</div>
              <div className="text-[15px] font-medium text-foreground leading-[1.4]">
                {hoursRows.slice(0, 2).map((r, i) => (
                  <div key={i}>
                    {r.day} <span className="text-foreground/55">{r.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(branch.latitude && branch.longitude) && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.08em] text-foreground/55 mb-1.5">Байршил</div>
              <div className="text-[15px] font-medium text-foreground leading-[1.4]">Газрын зураг дээр<br /><span className="text-foreground/55">{branch.latitude.toFixed(4)}, {branch.longitude.toFixed(4)}</span></div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2.5">
          {mapHref && (
            <a href={mapHref} target="_blank" rel="noopener noreferrer" className="h-12 px-5 rounded-full bg-foreground text-cream text-[15px] font-medium inline-flex items-center gap-2.5 hover:opacity-90 transition-opacity">
              Байршил <ArrowRight className="size-4" />
            </a>
          )}
          {branch.phone && (
            <a href={`tel:${branch.phone}`} className="h-12 px-5 rounded-full bg-white border border-foreground/15 text-foreground text-[15px] font-medium inline-flex items-center gap-2.5 hover:border-foreground/40 transition-colors">
              <Phone className="size-4" /> Утсаар холбогдох
            </a>
          )}
        </div>
      </div>
    </section>
  )
}

export default function BranchList({ branches, vendor }: { branches: any, vendor?: any }) {
  const pathname = usePathname() || ''
  const [query, setQuery] = useState('')
  const list: any[] = (branches || []).filter((b: any) => b?.isActive !== false)
  const total = list.length
  const vendorName = vendor?.name || 'Дэлгүүр'

  const filtered = useMemo(() => {
    if (!query.trim()) return list
    const q = query.trim().toLowerCase()
    return list.filter(b => [b.name, b.address, b.phone].filter(Boolean).some((v: string) => String(v).toLowerCase().includes(q)))
  }, [list, query])

  const featured = filtered.find(b => b.isDefault) || filtered[0]
  const rest = filtered.filter(b => b?.id !== featured?.id)

  return (
    <section className="bg-cream pt-6 pb-16">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <nav className="text-[13px] text-foreground/55 mb-6">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Салбарууд</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-7 lg:gap-14">
          <h1 className="text-5xl sm:text-7xl font-bold font-gilroy tracking-[-0.035em] leading-[0.95] text-foreground">
            Биечлэн<br />үзэх <span className="text-gradient">дуртай юу?</span>
          </h1>
          <div className="flex flex-col gap-4 pb-3.5">
            <p className="text-[16px] sm:text-[17px] leading-[1.55] text-foreground/60 max-w-md">
              {vendorName}-н {total} салбар танд үйлчилгээ үзүүлж байна. Бараагаа очиж үзэх, эсхүл шууд дэлгүүрээс худалдан авах боломжтой.
            </p>
            <div className="flex gap-8 pt-2">
              <div>
                <div className="text-[32px] font-semibold font-gilroy leading-none tracking-[-0.02em] text-foreground">{total}</div>
                <div className="text-[12px] text-foreground/55 mt-1.5">Салбар</div>
              </div>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-foreground/15 bg-[#F1EEE7]/40 py-20 px-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-white border border-foreground/10 inline-flex items-center justify-center mb-4">
              <MapPin className="size-[22px] text-foreground/55" />
            </div>
            <div className="text-base font-semibold text-foreground">{query ? 'Илэрц олдсонгүй' : 'Салбарын мэдээлэл хоосон байна'}</div>
            <div className="mt-1.5 text-[14px] text-foreground/55">{query ? `"${query}" гэсэн нэртэй салбар олдсонгүй. Өөр түлхүүр үг ашиглан дахин хайна уу.` : 'Яг одоогоор салбарын мэдээлэл байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу.'}</div>
          </div>
        ) : (
          <>
            {featured && <FeaturedBranch branch={featured} />}
            {rest.length > 0 && (
              <div className="mt-10">
                <div className="flex items-end justify-between mb-5 px-1">
                  <h2 className="text-[24px] sm:text-[26px] font-semibold font-gilroy tracking-[-0.015em] text-foreground inline-flex items-center gap-2.5">
                    <Clock className="size-5 text-foreground/40" />
                    Бусад салбарууд
                  </h2>
                  <span className="text-[13px] text-foreground/55">{rest.length} салбар</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {rest.map((b, i) => (
                    <BranchCard key={b.id} branch={b} index={i + 1} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
